/**
 * Whether a schema-driven link value is an origin-relative in-app path, and so
 * safe to hand to the app's router (wouter on web, expo-router on native).
 *
 * Artifact views are assembled from LLM-adjacent data, so a `javascript:` or
 * `data:` value could otherwise reach navigation, and a protocol-relative
 * `//host/path` would silently leave the app.
 *
 * Backslashes are rejected for the same reason `//` is: the WHATWG URL parser
 * treats `\` as an authority-position `/` for special schemes, so a browser
 * following a raw `/\evil.example.com` anchor canonicalizes it into a
 * cross-origin network-path reference. The login `returnTo` resolver in
 * `web/src/views/Auth.tsx` uses this same guard, since it defends the same
 * open-redirect class.
 *
 * Tab, newline and carriage return are rejected outright rather than stripped.
 * The parser removes them *before* parsing, so `/<tab>/evil.example.com` reads
 * as a plain path here and canonicalizes to `//evil.example.com` in the browser.
 * Rejecting keeps the string we validate identical to the string the caller
 * navigates to; no legitimate in-app path contains them.
 *
 * Shared because both clients apply the identical guard to the same server-built
 * `appLink` / `href` values, and the two must not drift.
 */
export const isInAppPath = (value: string): boolean =>
  value.startsWith("/") &&
  !value.startsWith("//") &&
  !value.includes("\\") &&
  !/[\t\n\r]/.test(value);
